import type {
  AlignValue,
  BgPositionValue,
  TextDecorationValue,
  TextTransformValue,
  WidthValue
} from 'core/helpers/styles'
import type { ElementBadge } from 'core/types/element/element_badge'
import type { ElementBullet } from 'core/types/element/element_bullet'
import type { ElementButton } from 'core/types/element/element_button'
import type { ElementCheckbox } from 'core/types/element/element_checkbox'
import type { ElementDatetime } from 'core/types/element/element_datetime'
import type { ElementDropdown } from 'core/types/element/element_dropdown'
import type { ElementEmbedCode } from 'core/types/element/element_embed_code'
import type { ElementHeader } from 'core/types/element/element_header'
import type { ElementHosts } from 'core/types/element/element_hosts'
import type { ElementIcon } from 'core/types/element/element_icon'
import type { ElementImage } from 'core/types/element/element_image'
import type { ElementImageFeature } from 'core/types/element/element_image_feature'
import type { ElementInput } from 'core/types/element/element_input'
import type { ElementLogo } from 'core/types/element/element_logo'
import type { ElementQuiz } from 'core/types/element/element_quiz'
import type { ElementReminder } from 'core/types/element/element_reminder'
import type { ElementText } from 'core/types/element/element_text'
import type { ElementTextarea } from 'core/types/element/element_textarea'
import type { ElementTimer } from 'core/types/element/element_timer'
import type { ElementVideo } from 'core/types/element/element_video'
import type { ElementWebinarLink } from 'core/types/element/element_webinar_link'
import Session from './session'

export const WIDGET_TYPES = {
  FLOATER: 'floater',
  MODAL: 'modal',
  EMBED: 'embed',
  THANK_YOU: 'thank_you',
  REGISTRATION_PAGE: 'registration_page'
} as const

export type WIDGET_TYPE = (typeof WIDGET_TYPES)[keyof typeof WIDGET_TYPES]

export type Webinar = {
  id: number
  account_id: number
  cid: string
  recaptcha_integration: {
    site_key: string
  }
  custom_fields_from_url?: string[]
}

export type WidgetElement =
  | ElementBadge
  | ElementBullet
  | ElementButton
  | ElementCheckbox
  | ElementDatetime
  | ElementDropdown
  | ElementEmbedCode
  | ElementHeader
  | ElementHosts
  | ElementIcon
  | ElementImage
  | ElementImageFeature
  | ElementInput
  | ElementLogo
  | ElementQuiz
  | ElementReminder
  | ElementText
  | ElementTextarea
  | ElementTimer
  | ElementVideo
  | ElementWebinarLink

export type Column = {
  id: number
  title: string
  elements: WidgetElement[]
  width: number
  options: {
    bg_color: string
    bg_image_url: string
    bg_image_position: BgPositionValue
    bg_image_position_advanced: string
    bg_image_size: string
    bg_image_size_advanced: string
    margin_top: number
    padding_top: number
    padding_right: number
    padding_bottom: number
    padding_left: number
    border_side: string
    border_size: string
    border_style: string
    border_color: string
    border_top_left: number
    border_top_right: number
    border_bottom_left: number
    border_bottom_right: number
    box_shadow: string
  }
}

export type Row = {
  id: number
  title: string
  columns: Column[]
  options: {
    column_gap: number
    width: WidthValue
    width_fixed: number
    width_percent: number
    bg_color: string
    bg_image_url: string
    bg_image_position: BgPositionValue
    bg_image_position_advanced: string
    bg_image_size: string
    bg_image_size_advanced: string
    align: AlignValue
    margin_top: number
    padding_top: number
    padding_right: number
    padding_bottom: number
    padding_left: number
    border_side: string
    border_size: string
    border_style: string
    border_color: string
    border_top_left: number
    border_top_right: number
    border_bottom_left: number
    border_bottom_right: number
    box_shadow: string
  }
}

export type Step = {
  id: number
  slug: string
  rows: Row[]
  success: boolean
}

type Widget = {
  id: number
  name: string
}

export type EmbedVersion = {
  id: number
}

export type HandlerIcon = {
  name: string
  code: string
  color: string
  size: number
}

type HandlerTextStyle = {
  text_color: string
  line_height: number
  font_family: string
  font_size: number
  font_weight: string
  text_align: AlignValue
  text_decoration: TextDecorationValue
  text_shadow: string
  text_transform: TextTransformValue
  letter_spacing: string
}

type Host = {
  avatar_url: string
  first_name: string
  last_name: string
}

type Version = {
  id: number
  name: string
  widget_id: number
  widget_type: WIDGET_TYPE
  steps: Step[]
  recaptcha_enabled: boolean
  cloudflare_turnstile_enabled: boolean
  cloudflare_turnstile_integration?: {
    site_key: string
  }
  widget: Widget
  webinar: Webinar
  formatted_sessions: Session[]
  hosts: Host[]
  options: {
    handler: {
      width: WidthValue
      width_fixed: number
      width_percent: number
      height: number
      align: AlignValue
      padding_top: number
      padding_right: number
      padding_bottom: number
      padding_left: number
      image_url: string
      bg_color: string
      logo_color: string
      close_cross_color: string
      border_side: string
      border_size: string
      border_style: string
      border_color: string
      border_top_left: number
      border_top_right: number
      border_bottom_right: number
      border_bottom_left: number
      left_icon: HandlerIcon
      right_icon: HandlerIcon
      text: HandlerTextStyle
      subtext: HandlerTextStyle & { margin_top: number }
      button_text: string
      button_subtext: string
    }
    layout: {
      open_by_default: boolean
      width: WidthValue
      width_fixed: number
      width_percent: number
      bg_color: string
      bg_image_url: string
      bg_image_position: BgPositionValue
      bg_image_position_advanced: string
      bg_image_size: string
      bg_image_size_advanced: string
      align: AlignValue
      margin_top: number
      padding_top: number
      padding_right: number
      padding_bottom: number
      padding_left: number
      border_side: string
      border_size: string
      border_style: string
      border_color: string
      border_top_left: string
      border_top_right: string
      border_bottom_right: string
      border_bottom_left: string
      box_shadow: string
    }
    close_button: {
      bg_color: string
      cross_color: string
    }
  }
}
export default Version

export enum ELEMENT_TYPE {
  Text = 'Widget::ElementText',
  Header = 'Widget::ElementHeader',
  Datetime = 'Widget::ElementDatetime',
  Timer = 'Widget::ElementTimer',
  Button = 'Widget::ElementButton',
  Video = 'Widget::ElementVideo',
  Image = 'Widget::ElementImage',
  Hosts = 'Widget::ElementHosts',
  Reminder = 'Widget::ElementReminder',
  WebinarLink = 'Widget::ElementWebinarLink',
  Icon = 'Widget::ElementIcon',
  Quiz = 'Widget::ElementQuiz',
  Dropdown = 'Widget::ElementDropdown',
  Input = 'Widget::ElementInput',
  Textarea = 'Widget::ElementTextarea',
  Checkbox = 'Widget::ElementCheckbox',
  Logo = 'Widget::ElementLogo',
  EmbedCode = 'Widget::ElementEmbedCode',
  Bullet = 'Widget::ElementBullet',
  ImageFeature = 'Widget::ElementImageFeature',
  Badge = 'Widget::ElementBadge'
}

export type ElementTypesMap = {
  [ELEMENT_TYPE.Text]: ElementText
  [ELEMENT_TYPE.Header]: ElementHeader
  [ELEMENT_TYPE.Datetime]: ElementDatetime
  [ELEMENT_TYPE.Timer]: ElementTimer
  [ELEMENT_TYPE.Button]: ElementButton
  [ELEMENT_TYPE.Video]: ElementVideo
  [ELEMENT_TYPE.Image]: ElementImage
  [ELEMENT_TYPE.Hosts]: ElementHosts
  [ELEMENT_TYPE.Reminder]: ElementReminder
  [ELEMENT_TYPE.WebinarLink]: ElementWebinarLink
  [ELEMENT_TYPE.Icon]: ElementIcon
  [ELEMENT_TYPE.Quiz]: ElementQuiz
  [ELEMENT_TYPE.Dropdown]: ElementDropdown
  [ELEMENT_TYPE.Input]: ElementInput
  [ELEMENT_TYPE.Textarea]: ElementTextarea
  [ELEMENT_TYPE.Checkbox]: ElementCheckbox
  [ELEMENT_TYPE.Logo]: ElementLogo
  [ELEMENT_TYPE.EmbedCode]: ElementEmbedCode
  [ELEMENT_TYPE.Bullet]: ElementBullet
  [ELEMENT_TYPE.ImageFeature]: ElementImageFeature
  [ELEMENT_TYPE.Badge]: ElementBadge
}
